<template>
  <div id="Topics">
    <b-container fluid>
      <b-row
        class="background background_filter page_header_row"
        :style="{ 'background-image': `url(${headerBackgroundImage})` }"
      >
        <b-col cols="12" class="u-non-blurred">
          <div class="page_header_inner">
            <div class="header_back_button" @click="back">
              <b-icon-chevron-left aria-hidden="true" /> Back
            </div>
            <div class="header_content_stack">
              <div class="header_breadcrumb" role="heading" aria-level="1">
                <span class="crumb_item">AP World History</span>
                <span class="crumb_sep" aria-hidden="true">→</span>
                <span class="crumb_item">{{ timePeriodHeaders.header }} ({{ timePeriodHeaders.subheader }})</span>
                <span v-if="headerUnitTitle" class="crumb_sep" aria-hidden="true">→</span>
                <span v-if="headerUnitTitle" class="crumb_item crumb_current">{{ headerUnitTitle }}</span>
              </div>
             
            </div>
                  
          </div>
          <p class="unit_header">{{ headerUnitTitle }} </p>
        </b-col>
      </b-row>
    </b-container>
    <b-container class="topics_container">
      <b-row
        v-for="unit in visibleUnits"
        :key="unit.unitHeader"
        class="topics_unit_row"
      >
        <b-col cols="12">
          <div class="topics_content_layout">
            <section class="topics_left_panel">
              <div
                class="topic_cards_grid"
                role="list"
                :aria-label="`${unit.unitHeader} topics`"
              >
                <button
                  v-for="region in mapRegionsForUnit(unit)"
                  :key="`card-${unit.unitHeader}-${region.topic.id}`"
                  type="button"
                  class="topic_card_tile"
                  :class="{ hovered: hoveredTopicId === region.topic.id, active: isFocusedTopic(region.topic, unit) }"
                  :style="topicCardStyle(region.topic)"
                  @mouseenter="setHoveredTopic(region.topic.id)"
                  @mouseleave="clearHoveredTopic"
                  @focus="setHoveredTopic(region.topic.id)"
                  @blur="clearHoveredTopic"
                  @click="next(region.topic)"
                  :aria-label="`Open topic ${region.topic.title}`"
                >
                  <span
                    class="topic_card_accent"
                    :style="{ backgroundColor: getTopicColor(region.topic) }"
                    aria-hidden="true"
                  ></span>
                  <span class="topic_card_body">
                    <span class="topic_card_title">{{ region.topic.title }}</span>
                    <span v-if="region.topic.timespan" class="topic_card_time">
                      ({{ region.topic.timespan }})
                    </span>
                  </span>
                </button>
              </div>
            </section>

            <section class="topics_right_panel">
              <div
                class="topics_map"
                role="region"
                :aria-label="`${unit.unitHeader} topic map`"
              >
                <svg class="world_svg" viewBox="0 0 1000 520" aria-hidden="true">
                  <rect
                    x="0"
                    y="0"
                    width="1000"
                    height="520"
                    class="ocean"
                  ></rect>
                  <path
                    v-if="worldLandPath"
                    class="world_land"
                    :d="worldLandPath"
                  />
                  <path
                    v-if="worldBorderPath"
                    class="world_borders"
                    :d="worldBorderPath"
                  />

                  <path
                    v-for="region in nonSpecialOverlayRegionsForUnit(unit)"
                    :key="`region-fill-${unit.unitHeader}-${region.topic.id}`"
                    class="map_highlight"
                    :class="{ hovered: hoveredTopicId === region.topic.id, world_outline: isWorldOutlineRegion(region) }"
                    :style="[regionStyle(region), isIndustrialFocused(unit) ? { opacity: 0.1 } : null]"
                    :d="region.path"
                    tabindex="0"
                    role="button"
                    @mouseenter="setHoveredRegion(region, $event)"
                    @mousemove="moveTooltip($event)"
                    @mouseleave="clearHoveredTopic"
                    @focus="setHoveredRegion(region, $event)"
                    @blur="clearHoveredTopic"
                    @click="next(region.topic)"
                    @keydown.enter.prevent="next(region.topic)"
                    @keydown.space.prevent="next(region.topic)"
                    :aria-label="`Open topic ${region.topic.title}`"
                  >
                    <title>
                      {{ region.topic.title }}
                      {{
                        region.topic.timespan ? `(${region.topic.timespan})` : ""
                      }}
                    </title>
                  </path>

                  <g
                    v-for="region in overlapAwareRegionsForUnit(unit)"
                    :key="`overlap-aware-${unit.unitHeader}-${region.topic.id}`"
                    class="overlap_aware_layer"
                    :class="{ hovered: hoveredTopicId === region.topic.id, active: isFocusedTopic(region.topic, unit), ghost: !isFocusedTopic(region.topic, unit) }"
                    :style="topicLayerStyle(region.topic, unit)"
                    tabindex="0"
                    role="button"
                    @mouseenter="setHoveredRegion(region, $event)"
                    @mousemove="moveTooltip($event)"
                    @mouseleave="clearHoveredTopic"
                    @focus="setHoveredRegion(region, $event)"
                    @blur="clearHoveredTopic"
                    @click="next(region.topic)"
                    @keydown.enter.prevent="next(region.topic)"
                    @keydown.space.prevent="next(region.topic)"
                    :aria-label="`Open topic ${region.topic.title}`"
                  >
                    <path
                      class="overlap_contour"
                      :style="overlapContourStyle(region, unit)"
                      :d="region.path"
                    />

                    <template v-if="topicRenderMode(region.topic) === 'idea-network'">
                      <path
                        v-for="(route, index) in enlightenmentRoutes"
                        :key="`idea-route-${region.topic.id}-${index}`"
                        class="idea_route"
                        :d="route"
                      />
                      <circle
                        v-for="node in enlightenmentNodes"
                        :key="`idea-node-glow-${region.topic.id}-${node.id}`"
                        class="idea_node_glow"
                        :cx="node.x"
                        :cy="node.y"
                        :r="node.glowR"
                      />
                      <circle
                        v-for="node in enlightenmentNodes"
                        :key="`idea-node-${region.topic.id}-${node.id}`"
                        class="idea_node"
                        :cx="node.x"
                        :cy="node.y"
                        :r="node.r"
                      />
                    </template>

                    <template v-else-if="topicRenderMode(region.topic) === 'heatmap-contour'">
                      <ellipse
                        v-for="zone in industrialHeatZones"
                        :key="`heat-zone-${region.topic.id}-${zone.id}`"
                        class="industrial_heat_zone"
                        :cx="zone.x"
                        :cy="zone.y"
                        :rx="zone.rx"
                        :ry="zone.ry"
                      />
                      <path
                        v-for="(arc, index) in industrialSpreadArcs"
                        :key="`industrial-spread-arc-${region.topic.id}-${index}`"
                        class="industrial_spread_arc"
                        :d="arc"
                      />
                      <circle
                        v-for="node in industrialSpreadNodes"
                        :key="`industrial-spread-node-${region.topic.id}-${node.id}`"
                        class="industrial_spread_node"
                        :cx="node.x"
                        :cy="node.y"
                        :r="node.r"
                      />
                      <text
                        v-for="node in industrialSpreadNodes"
                        :key="`industrial-city-label-${region.topic.id}-${node.id}`"
                        class="industrial_city_label"
                        :x="node.x + node.labelDx"
                        :y="node.y + node.labelDy"
                      >{{ node.name }}</text>
                    </template>

                    <template v-else-if="topicRenderMode(region.topic) === 'global-flow'">
                      <path
                        v-for="(arc, index) in consequenceFlowArcs"
                        :key="`flow-arc-${region.topic.id}-${index}`"
                        class="global_flow_arc"
                        :d="arc"
                      />
                      <circle
                        v-for="node in consequenceFlowNodes"
                        :key="`flow-node-${region.topic.id}-${node.id}`"
                        class="global_flow_node"
                        :cx="node.x"
                        :cy="node.y"
                        :r="node.r"
                      />
                    </template>

                    <template v-else-if="topicRenderMode(region.topic) === 'planned-grid'">
                      <path
                        v-for="(segment, index) in stateGridSegments"
                        :key="`state-grid-${region.topic.id}-${index}`"
                        class="state_grid_segment"
                        :d="segment"
                      />
                      <path
                        v-for="(corridor, index) in statePlannedCorridors"
                        :key="`state-corridor-${region.topic.id}-${index}`"
                        class="state_corridor"
                        :d="corridor"
                      />
                    </template>

                    <title>
                      {{ region.topic.title }}
                      {{
                        region.topic.timespan ? `(${region.topic.timespan})` : ""
                      }}
                    </title>
                  </g>

                  <g
                    v-for="region in thirteenColoniesRegionsForUnit(unit)"
                    :key="`thirteen-colonies-${unit.unitHeader}-${region.topic.id}`"
                    class="thirteen_colonies_overlay"
                    :class="{ hovered: hoveredTopicId === region.topic.id }"
                    tabindex="0"
                    role="button"
                    @mouseenter="setHoveredRegion(region, $event)"
                    @mousemove="moveTooltip($event)"
                    @mouseleave="clearHoveredTopic"
                    @focus="setHoveredRegion(region, $event)"
                    @blur="clearHoveredTopic"
                    @click="next(region.topic)"
                    @keydown.enter.prevent="next(region.topic)"
                    @keydown.space.prevent="next(region.topic)"
                    :aria-label="`Open topic ${region.topic.title}`"
                  >
                    <path
                      class="thirteen_colonies_area"
                      :style="regionStyle(region.topic)"
                      :d="thirteenColoniesPath"
                    />
                    <title>
                      {{ region.topic.title }}
                      {{
                        region.topic.timespan ? `(${region.topic.timespan})` : ""
                      }}
                    </title>
                  </g>

                  <g
                    v-for="region in franceOnlyRegionsForUnit(unit)"
                    :key="`france-only-${unit.unitHeader}-${region.topic.id}`"
                    class="france_only_overlay"
                    :class="{ hovered: hoveredTopicId === region.topic.id }"
                    tabindex="0"
                    role="button"
                    @mouseenter="setHoveredRegion(region, $event)"
                    @mousemove="moveTooltip($event)"
                    @mouseleave="clearHoveredTopic"
                    @focus="setHoveredRegion(region, $event)"
                    @blur="clearHoveredTopic"
                    @click="next(region.topic)"
                    @keydown.enter.prevent="next(region.topic)"
                    @keydown.space.prevent="next(region.topic)"
                    :aria-label="`Open topic ${region.topic.title}`"
                  >
                    <path
                      class="france_only_area"
                      :style="regionStyle(region.topic)"
                      :d="region.path"
                    />
                    <title>
                      {{ region.topic.title }}
                      {{
                        region.topic.timespan ? `(${region.topic.timespan})` : ""
                      }}
                    </title>
                  </g>

                  <g
                    v-for="region in wwiiRegionsForUnit(unit)"
                    :key="`wwii-overlay-${unit.unitHeader}-${region.topic.id}`"
                    class="wwii_overlay"
                    :class="{ hovered: hoveredTopicId === region.topic.id }"
                    tabindex="0"
                    role="button"
                    @mouseenter="setHoveredRegion(region, $event)"
                    @mousemove="moveTooltip($event)"
                    @mouseleave="clearHoveredTopic"
                    @focus="setHoveredRegion(region, $event)"
                    @blur="clearHoveredTopic"
                    @click="next(region.topic)"
                    @keydown.enter.prevent="next(region.topic)"
                    @keydown.space.prevent="next(region.topic)"
                    :aria-label="`Open topic ${region.topic.title}`"
                  >
                    <path
                      class="wwii_land_zone"
                      :style="regionStyle(region.topic)"
                      :d="region.path"
                    />
                    <path
                      v-for="(zone, index) in wwiiAtlanticZones"
                      :key="`wwii-atlantic-${region.topic.id}-${index}`"
                      class="wwii_ocean_zone"
                      :style="{ fill: hexToRgba(getTopicColor(region.topic), hoveredTopicId === region.topic.id ? 0.28 : 0.18), stroke: hexToRgba(getTopicColor(region.topic), hoveredTopicId === region.topic.id ? 0.52 : 0.35) }"
                      :d="zone"
                    />
                    <title>
                      {{ region.topic.title }}
                      {{
                        region.topic.timespan ? `(${region.topic.timespan})` : ""
                      }}
                    </title>
                  </g>

                  <g
                    v-for="region in silkRoadRegionsForUnit(unit)"
                    :key="`silk-overlay-${unit.unitHeader}-${region.topic.id}`"
                    class="silk_road_overlay"
                    :class="{ hovered: hoveredTopicId === region.topic.id }"
                    tabindex="0"
                    role="button"
                    @mouseenter="setHoveredRegion(region, $event)"
                    @mousemove="moveTooltip($event)"
                    @mouseleave="clearHoveredTopic"
                    @focus="setHoveredRegion(region, $event)"
                    @blur="clearHoveredTopic"
                    @click="next(region.topic)"
                    @keydown.enter.prevent="next(region.topic)"
                    @keydown.space.prevent="next(region.topic)"
                    :aria-label="`Open topic ${region.topic.title}`"
                  >
                    <path
                      v-for="(segment, index) in silkRoadRouteSegments"
                      :key="`silk-route-${region.topic.id}-${index}`"
                      class="map_silk_route"
                      :style="{ stroke: getTopicColor(region.topic) }"
                      :d="segment"
                    />

                    <path
                      v-for="(river, index) in silkRoadRiverSegments"
                      :key="`silk-river-${region.topic.id}-${index}`"
                      class="silk_road_river"
                      :d="river"
                    />

                    <circle
                      v-for="city in silkRoadCities"
                      :key="`silk-city-dot-${region.topic.id}-${city.name}`"
                      class="silk_city_dot"
                      :style="{ fill: getTopicColor(region.topic) }"
                      :cx="city.x"
                      :cy="city.y"
                      r="5.5"
                    />

                    <text
                      v-for="city in silkRoadCities"
                      :key="`silk-city-label-${region.topic.id}-${city.name}`"
                      class="silk_city_label"
                      :x="city.x + city.labelDx"
                      :y="city.y + city.labelDy"
                    >
                      {{ city.name }}
                    </text>

                    <text
                      v-for="label in silkRoadRegionLabels"
                      :key="`silk-region-label-${region.topic.id}-${label.text}`"
                      class="silk_region_label"
                      :x="label.x"
                      :y="label.y"
                    >
                      {{ label.text }}
                    </text>

                    <text
                      v-for="label in silkRoadWaterLabels"
                      :key="`silk-water-label-${region.topic.id}-${label.text}`"
                      class="silk_water_label"
                      :x="label.x"
                      :y="label.y"
                    >
                      {{ label.text }}
                    </text>

                    <title>
                      {{ region.topic.title }}
                      {{
                        region.topic.timespan ? `(${region.topic.timespan})` : ""
                      }}
                    </title>
                  </g>
                </svg>

                <div
                  v-if="mapBadgeForUnit(unit)"
                  class="map_mode_badge"
                  :style="{ '--badge-accent': mapBadgeForUnit(unit).accentColor }"
                >
                  <span class="badge_dot" aria-hidden="true"></span>
                  <div class="badge_text">
                    <p class="badge_title">{{ mapBadgeForUnit(unit).title }}</p>
                    <p class="badge_subtitle">{{ mapBadgeForUnit(unit).dateRange }}</p>
                  </div>
                </div>

                <div
                  v-if="hoverTooltip.visible"
                  class="map_tooltip"
                  :style="{
                    left: `${hoverTooltip.x}px`,
                    top: `${hoverTooltip.y}px`,
                    borderColor: hoverTooltip.color
                  }"
                >
                  <p class="tooltip_title">{{ hoverTooltip.title }}</p>
                  <p v-if="hoverTooltip.timespan" class="tooltip_time">
                    {{ hoverTooltip.timespan }}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import * as d3 from "d3";
import * as topojson from "topojson";
import store from "@/store";
import countries from "@/countries.json";
import storeTopic from "@/store/topic.js";
import { pushRoute, goToTopic } from "@/router/navigation";

export default {
  name: "Topics",
  data() {
    return {
      hoveredTopicId: null,
      unitRegionCache: {},
      hoverTooltip: {
        visible: false,
        x: 0,
        y: 0,
        title: "",
        timespan: "",
        color: "#f59e0b"
      },
      tooltipRafId: null,
      pendingTooltipPosition: null,
      activeMapRect: null,
      silkRoadRouteSegments: [
        // Constantinople → Baghdad
        "M580 146 C596 152 610 160 623 168",
        // Cairo → Baghdad
        "M587 177 C600 174 612 171 623 168",
        // Baghdad → Tehran
        "M623 168 C631 165 637 163 643 161",
        // Tehran → Bukhara → Samarkand (northern Central Asia corridor)
        "M643 161 C657 156 670 153 679 150 C681 150 684 150 686 150",
        // Samarkand → split point in eastern China via Central Asia
        "M686 150 C716 149 748 149 778 150 C792 151 802 152 810 154",
        // Beijing branch (north from split)
        "M810 154 C815 152 819 150 823 149",
        // Shanghai branch (south from split)
        "M810 154 C821 161 829 167 837 173",
        // Southern branch: Samarkand area → Delhi
        "M686 150 C697 160 706 171 714 181"
      ],
      silkRoadRiverSegments: [
        // Euphrates (west of Tigris, near Baghdad)
        "M620 158 C621 163 621 168 620 174",
        // Tigris (through Baghdad)
        "M625 158 C625 163 625 168 625 174"
      ],
      thirteenColoniesPath: "",
      wwiiAtlanticZones: [
        "M315 176 C355 152 407 152 447 177 C486 202 492 241 456 262 C419 282 365 283 326 258 C289 233 284 197 315 176 Z",
        "M356 278 C395 258 447 257 490 278 C531 299 536 337 498 359 C459 381 405 381 363 360 C323 340 319 301 356 278 Z"
      ],
      silkRoadCities: [
        { name: "Constantinople", x: 580, y: 146, labelDx: -105, labelDy: 0 },
        { name: "Cairo", x: 587, y: 177, labelDx: -44, labelDy: 16 },
        { name: "Baghdad", x: 623, y: 168, labelDx: -10, labelDy: 24 },
        { name: "Tehran", x: 643, y: 161, labelDx: -38, labelDy: -10 },
        { name: "Bukhara", x: 673, y: 150, labelDx: -45, labelDy: -14 },
        { name: "Samarkand", x: 686, y: 150, labelDx: 6, labelDy: -10 },
        { name: "Delhi", x: 714, y: 181, labelDx: 8, labelDy: -6 },
        { name: "Beijing", x: 823, y: 149, labelDx: -38, labelDy: -12 },
        { name: "Shanghai", x: 837, y: 173, labelDx: 6, labelDy: 18 }
      ],
      topicVisualProfiles: [
        {
          id: "enlightenment",
          title: "Enlightenment",
          dateRange: "c. 1680-1800",
          accentColor: "#E7B95A",
          palette: ["#F5E6C8", "#E7B95A", "rgba(231, 185, 90, 0.25)"],
          mapTreatmentType: "idea-network",
          overlapGroup: "europe-industrial-modernity",
          overlapIndex: 0,
          contourOffsetPx: 4,
          highlightedRegions: ["europe"],
          nodes: ["paris", "london", "edinburgh", "amsterdam", "geneva"],
          routes: ["ideas"],
          optionalSymbols: []
        },
        {
          id: "industrial-revolutions",
          title: "Industrial Revolutions",
          dateRange: "c. 1760-1840",
          accentColor: "#C46A28",
          palette: ["#C46A28", "#6B7280", "#2A2A2A"],
          mapTreatmentType: "heatmap-contour",
          overlapGroup: "europe-industrial-modernity",
          overlapIndex: 1,
          contourOffsetPx: 8,
          highlightedRegions: ["europe"],
          nodes: ["britain", "ruhr", "nfrance", "belgium", "newengland"],
          routes: [],
          optionalSymbols: ["factory"]
        },
        {
          id: "consequences-industrialization",
          title: "Consequences of Industrialization",
          dateRange: "c. 1800-1914",
          accentColor: "#4CC9B0",
          palette: ["#4CC9B0", "#5BC0EB", "#4361EE"],
          mapTreatmentType: "global-flow",
          overlapGroup: "europe-industrial-modernity",
          overlapIndex: 2,
          contourOffsetPx: 12,
          highlightedRegions: ["europe", "americas", "africa", "eastAsia"],
          nodes: ["ports"],
          routes: ["global-trade"],
          optionalSymbols: []
        },
        {
          id: "state-sponsored-industrialization",
          title: "State-Sponsored Industrialization",
          dateRange: "c. 1850-1940",
          accentColor: "#D84A4A",
          palette: ["#D84A4A", "#8B1E3F", "#4B5563"],
          mapTreatmentType: "planned-grid",
          overlapGroup: "europe-industrial-modernity",
          overlapIndex: 3,
          contourOffsetPx: 14,
          highlightedRegions: ["russiaCentralAsia", "eastAsia"],
          nodes: ["state-centers"],
          routes: ["corridors"],
          optionalSymbols: ["vectors"]
        }
      ],
      enlightenmentNodes: [
        { id: "paris", x: 532, y: 145, r: 3.2, glowR: 17 },
        { id: "london", x: 520, y: 134, r: 2.9, glowR: 13 },
        { id: "edinburgh", x: 517, y: 126, r: 2.5, glowR: 11 },
        { id: "amsterdam", x: 535, y: 132, r: 2.4, glowR: 11 },
        { id: "geneva", x: 540, y: 147, r: 2.3, glowR: 10 }
      ],
      enlightenmentRoutes: [
        "M520 134 C525 132 530 132 535 132",
        "M535 132 C538 136 540 141 540 147",
        "M540 147 C536 146 534 145 532 145",
        "M517 126 C519 129 520 131 520 134",
        "M532 145 C529 141 525 137 520 134"
      ],
      industrialHeatZones: [
        { id: "newengland",x: 302, y: 137, rx: 15, ry: 10 },
        { id: "pittsburgh",x: 278, y: 143, rx: 12, ry: 8  }
      ],
      industrialSpreadNodes: [
        { id: "new-england",x: 302, y: 142, r: 3.0, name: "New England", labelDx: 6,   labelDy: -8 },
        { id: "pittsburgh", x: 278, y: 148, r: 2.8, name: "Pittsburgh",  labelDx: -58, labelDy: -8 }
      ],
      industrialSpreadArcs: [],
      consequenceFlowNodes: [
        { id: "eu", x: 534, y: 140, r: 3.3 },
        { id: "americas", x: 348, y: 166, r: 3 },
        { id: "west-africa", x: 500, y: 214, r: 2.8 },
        { id: "india", x: 650, y: 190, r: 2.8 },
        { id: "east-asia", x: 792, y: 157, r: 3 }
      ],
      consequenceFlowArcs: [
        "M534 140 C490 126 420 126 348 166",
        "M534 140 C522 156 512 181 500 214",
        "M534 140 C572 150 615 167 650 190",
        "M534 140 C610 131 705 130 792 157"
      ],
      stateGridSegments: [
        "M655 120 L730 120",
        "M658 130 L735 130",
        "M662 140 L739 140",
        "M666 150 L744 150",
        "M670 160 L749 160"
      ],
      statePlannedCorridors: [
        "M640 145 L706 141 L761 150",
        "M761 150 L797 152 L824 154",
        "M744 166 L778 176 L810 192"
      ],
      topicColors: [
        "#4f46e5",
        "#0ea5e9",
        "#16a34a",
        "#f59e0b",
        "#ef4444",
        "#a855f7",
        "#14b8a6",
        "#f97316",
        "#3b82f6",
        "#22c55e"
      ],
      worldLandPath: "",
      worldBorderPath: "",
      geoPath: null,
      countryGeometries: [],
      geometryById: new Map(),
      regionCountryMap: {
        franceOnly: ["FRA"],
        thirteenColonies: ["USA"],
        wwii: [
          // Europe active combat zones
          "GBR", "FRA", "NLD", "BEL", "LUX", "DEU", "POL", "CZE", "SVK", "AUT",
          "HUN", "ROU", "BGR", "GRC", "ITA", "DNK", "NOR", "SWE", "FIN",
          "HRV", "SVN", "BIH", "SRB", "MNE", "ALB", "MKD",
          // Western Soviet Union and Baltics
          "RUS", "UKR", "BLR", "EST", "LVA", "LTU", "MDA",
          // East and Southeast Asia active zones
          "JPN", "CHN", "PRK", "KOR", "VNM", "THA", "MYS", "MMR", "PHL", "IDN",
          // Pacific island theaters (country-level approximation)
          "PNG", "SLB", "VUT", "FJI",
          // North Africa and Mediterranean
          "EGY", "LBY", "TUN", "DZA", "MAR",
          // Major contributors / logistics hubs
          "IND", "PAK", "BGD", "NPL", "BTN", "LKA",
          "NGA", "KEN", "SEN", "TCD", "COD",
          "BRA"
        ],
        mongolEmpire: [
          // Eastern Europe and western steppe frontier
          "POL", "BLR", "UKR", "MDA", "ROU", "BGR",
          // Anatolia, Caucasus, and Persia
          "TUR", "ARM", "AZE", "GEO", "IRN", "IRQ", "SYR",
          // Core Eurasian steppe and successor khanates
          "RUS", "KAZ", "UZB", "TKM", "KGZ", "TJK", "AFG",
          // South and East Asian extent
          "PAK", "IND", "NPL", "BTN", "CHN", "MNG", "PRK", "KOR"
        ],
        silkRoad: [
          "CHN",
          "MNG",
          "KAZ",
          "KGZ",
          "UZB",
          "TKM",
          "TJK",
          "AFG",
          "PAK",
          "IRN",
          "IRQ",
          "SYR",
          "TUR"
        ],
        europe: [
          "GBR",
          "IRL",
          "PRT",
          "ESP",
          "FRA",
          "BEL",
          "NLD",
          "DEU",
          "CHE",
          "AUT",
          "ITA",
          "DNK",
          "NOR",
          "SWE",
          "FIN",
          "POL",
          "CZE",
          "SVK",
          "HUN",
          "ROU",
          "BGR",
          "GRC",
          "HRV",
          "SVN",
          "BIH",
          "SRB",
          "ALB",
          "MKD",
          "MNE",
          "UKR",
          "BLR",
          "EST",
          "LVA",
          "LTU",
          "ISL",
          "MDA",
          "TUR"
        ],
        china: ["CHN"],
        africa: [
          "MAR",
          "DZA",
          "TUN",
          "LBY",
          "EGY",
          "MRT",
          "MLI",
          "NER",
          "TCD",
          "SDN",
          "SEN",
          "GMB",
          "GIN",
          "GNB",
          "SLE",
          "LBR",
          "CIV",
          "GHA",
          "TGO",
          "BEN",
          "NGA",
          "CMR",
          "CAF",
          "GNQ",
          "GAB",
          "COG",
          "COD",
          "ETH",
          "ERI",
          "DJI",
          "SOM",
          "KEN",
          "UGA",
          "RWA",
          "BDI",
          "TZA",
          "AGO",
          "ZMB",
          "MWI",
          "MOZ",
          "ZWE",
          "BWA",
          "NAM",
          "ZAF",
          "LSO",
          "SWZ",
          "MDG"
        ],
        mesoamerica: ["MEX", "GTM", "BLZ", "SLV", "HND", "NIC", "CRI", "PAN"],
        southSoutheastAsia: [
          "IND",
          "PAK",
          "BGD",
          "LKA",
          "NPL",
          "BTN",
          "AFG",
          "MMR",
          "THA",
          "KHM",
          "LAO",
          "VNM",
          "MYS",
          "SGP",
          "IDN",
          "BRN",
          "PHL",
          "TLS"
        ],
        darAlIslam: [
          "TUR",
          "SYR",
          "IRQ",
          "JOR",
          "LBN",
          "ISR",
          "SAU",
          "YEM",
          "OMN",
          "ARE",
          "QAT",
          "BHR",
          "KWT",
          "IRN",
          "AFG",
          "PAK",
          "EGY",
          "LBY",
          "TUN",
          "DZA",
          "MAR"
        ],
        eastAsia: ["CHN", "JPN", "KOR", "PRK", "MNG", "TWN"],
        americas: [
          "CAN",
          "USA",
          "MEX",
          "GTM",
          "BLZ",
          "SLV",
          "HND",
          "NIC",
          "CRI",
          "PAN",
          "CUB",
          "DOM",
          "HTI",
          "JAM",
          "BRA",
          "ARG",
          "CHL",
          "PER",
          "COL",
          "VEN",
          "ECU",
          "BOL",
          "PRY",
          "URY",
          "GUY",
          "SUR"
        ],
        southAmerica: [
          "ARG",
          "BOL",
          "BRA",
          "CHL",
          "COL",
          "ECU",
          "GUY",
          "PRY",
          "PER",
          "SUR",
          "URY",
          "VEN"
        ],
        russiaCentralAsia: [
          "RUS",
          "KAZ",
          "UZB",
          "TKM",
          "KGZ",
          "TJK",
          "AZE",
          "ARM",
          "GEO"
        ],
        oceania: ["AUS", "NZL", "PNG", "FJI", "SLB", "VUT"]
      }
    };
  },
  computed: {
    headerBackgroundImage() {
      try {
        return this.timePeriodHeaders?.img
          ? require(`@/assets/${this.timePeriodHeaders.img}`)
          : "";
      } catch (e) {
        return "";
      }
    },
    timePeriodHeaders() {
      return store.state.timePeriodHeaders[store.state.currentTimePeriod];
    },
    units() {
      return store.state.units;
    },
    selectedUnitHeader() {
      const selectedUnitRaw = this.$route?.query?.unit;
      const selectedUnit = Array.isArray(selectedUnitRaw)
        ? selectedUnitRaw[0]
        : selectedUnitRaw;

      if (typeof selectedUnit !== "string") {
        return "";
      }

      try {
        return decodeURIComponent(selectedUnit).replace(/\+/g, " ");
      } catch (e) {
        return selectedUnit.replace(/\+/g, " ");
      }
    },
    unitsArray() {
      return Array.isArray(this.units)
        ? this.units
        : Object.values(this.units || {});
    },
    visibleUnits() {
      if (!this.unitsArray.length) {
        return [];
      }

      const normalizedSelected = this.selectedUnitHeader.trim().toLowerCase();
      if (!normalizedSelected) {
        return [this.unitsArray[0]];
      }

      const matchedUnit = this.unitsArray.find(unit =>
        String(unit?.unitHeader || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ")
          .startsWith(normalizedSelected)
      );

      return [matchedUnit || this.unitsArray[0]];
    },
    headerUnitTitle() {
      const visibleUnit = this.visibleUnits[0];
      const visibleHeader = String(visibleUnit?.unitHeader || "").trim();
      if (visibleHeader) {
        return visibleHeader;
      }

      return this.selectedUnitHeader.trim();
    }
  },
  watch: {
    units: {
      handler() {
        this.rebuildUnitRegionCache();
      },
      deep: true,
      immediate: true
    },
    geoPath(newPath) {
      if (newPath) {
        this.rebuildUnitRegionCache();
      }
    },
    "$route.params.period"() {
      this.rebuildUnitRegionCache();
    }
  },
  methods: {
    getUnitCacheKey(unit) {
      return String(unit?.id || unit?.unitHeader || "unknown-unit");
    },
    setActiveMapRect(event) {
      const mapContainer =
        event?.currentTarget?.closest?.(".topics_map") ||
        event?.target?.closest?.(".topics_map");
      if (!mapContainer) {
        return;
      }
      this.activeMapRect = mapContainer.getBoundingClientRect();
    },
    hashTopicId(topicId) {
      const value = String(topicId || "");
      let hash = 0;
      for (let index = 0; index < value.length; index += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
      }
      return Math.abs(hash);
    },
    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    },
    getTopicVisualProfile(topic) {
      if (!topic) {
        return null;
      }

      const topicId =
        typeof topic === "object" && topic !== null
          ? String(topic.id || "")
          : String(topic);
      const directById = this.topicVisualProfiles.find(profile => profile.id === topicId);
      if (directById) {
        return directById;
      }

      const topicText =
        typeof topic === "object" && topic !== null
          ? this.normalizeText(topic.title)
          : this.normalizeText(topic);

      if (!topicText) {
        return null;
      }

      if (
        topicText.includes("enlightenment") ||
        topicText.includes("englightenment")
      ) {
        return this.topicVisualProfiles.find(profile => profile.id === "enlightenment") || null;
      }
      if (
        topicText.includes("industrial revolutions") ||
        topicText.includes("industrial revolution")
      ) {
        return (
          this.topicVisualProfiles.find(
            profile => profile.id === "industrial-revolutions"
          ) || null
        );
      }
      if (
        topicText.includes("consequences of industrialization") ||
        (topicText.includes("consequence") && topicText.includes("industrialization"))
      ) {
        return (
          this.topicVisualProfiles.find(
            profile => profile.id === "consequences-industrialization"
          ) || null
        );
      }
      if (
        topicText.includes("state-sponsored industrialization") ||
        topicText.includes("state sponsored industrialization")
      ) {
        return (
          this.topicVisualProfiles.find(
            profile => profile.id === "state-sponsored-industrialization"
          ) || null
        );
      }

      return null;
    },
    isOverlapAwareTopic(topic) {
      const profile = this.getTopicVisualProfile(topic);
      return Boolean(profile?.overlapGroup);
    },
    topicRenderMode(topic) {
      const profile = this.getTopicVisualProfile(topic);
      return profile?.mapTreatmentType || "default-fill";
    },
    getTopicColor(topicOrId) {
      const profile = this.getTopicVisualProfile(topicOrId);
      if (profile?.accentColor) {
        return profile.accentColor;
      }

      const topicTitle =
        typeof topicOrId === "object" && topicOrId !== null
          ? String(topicOrId.title || "").toLowerCase()
          : "";
      if (
        topicTitle.includes("american revolution") ||
        topicTitle.includes("american revoltion") ||
        topicTitle.includes("american independence") ||
        topicTitle.includes("thirteen colonies")
      ) {
        return "#14b8a6";
      }
      if (topicTitle.includes("french revolution")) {
        return "#ef4444";
      }
      if (topicTitle.includes("mongol") || topicTitle.includes("yuan")) {
        return "#c83940";
      }

      const topicId =
        typeof topicOrId === "object" && topicOrId !== null
          ? topicOrId.id
          : topicOrId;
      const hash = this.hashTopicId(topicId);
      return this.topicColors[hash % this.topicColors.length];
    },
    hexToRgba(hex, alpha) {
      const clean = String(hex || "")
        .replace("#", "")
        .trim();
      if (clean.length !== 6) {
        return `rgba(245, 158, 11, ${alpha})`;
      }
      const red = parseInt(clean.slice(0, 2), 16);
      const green = parseInt(clean.slice(2, 4), 16);
      const blue = parseInt(clean.slice(4, 6), 16);
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    regionStyle(topic) {
      const region = topic && typeof topic === "object" && topic.topic ? topic : null;
      const topicValue = region ? region.topic : topic;
      const topicId =
        typeof topicValue === "object" && topicValue !== null ? topicValue.id : topicValue;
      const color = this.getTopicColor(topicValue);
      const isHovered = this.hoveredTopicId === topicId;
      const isWorldOutline = region
        ? this.isWorldOutlineRegion(region)
        : this.isWorldOutlineTopic(topicValue);

      if (isWorldOutline) {
        return {
          fill: "transparent",
          stroke: isHovered ? color : "transparent",
          strokeWidth: isHovered ? 2.35 : 0,
          pointerEvents: "all"
        };
      }

      return {
        fill: this.hexToRgba(color, isHovered ? 0.72 : 0.5),
        stroke: isHovered ? "#ffffff" : this.hexToRgba(color, 0.95),
        strokeWidth: isHovered ? 2 : 1.25
      };
    },
    isWorldOutlineTopic(topic) {
      const title = this.normalizedTitle(topic);
      return (
        title.includes("cold war") ||
        title.includes("wwii") ||
        title.includes("world war ii") ||
        title.includes("second world war") ||
        title.includes("world war 2") ||
        title.includes("wwi") ||
        title.includes("world war i") ||
        title.includes("first world war") ||
        title.includes("world war 1")
      );
    },
    isWorldOutlineRegion(region) {
      if (!region) {
        return false;
      }
      return region.regionKey === "world" || this.isWorldOutlineTopic(region.topic);
    },
    mapFocusTopicIdForUnit(unit) {
      const overlapRegions = this.overlapAwareRegionsForUnit(unit);
      if (!overlapRegions.length) {
        return null;
      }
      if (
        this.hoveredTopicId &&
        overlapRegions.some(region => region.topic.id === this.hoveredTopicId)
      ) {
        return this.hoveredTopicId;
      }
      return overlapRegions[0].topic.id;
    },
    isFocusedTopic(topic, unit) {
      const topicId = typeof topic === "object" && topic !== null ? topic.id : topic;
      if (!topicId) {
        return false;
      }
      return this.mapFocusTopicIdForUnit(unit) === topicId;
    },
    mapBadgeForUnit(unit) {
      const allRegions = this.mapRegionsForUnit(unit);
      if (!allRegions.length) {
        return null;
      }

      if (!this.hoveredTopicId) {
        return null;
      }

      let badgeTopic = null;

      if (this.hoveredTopicId) {
        const hoveredRegion = allRegions.find(
          region => region.topic.id === this.hoveredTopicId
        );
        if (hoveredRegion) {
          badgeTopic = hoveredRegion.topic;
        }
      }

      if (!badgeTopic) {
        return null;
      }

      const profile = this.getTopicVisualProfile(badgeTopic);
      if (profile) {
        return profile;
      }

      return {
        title: badgeTopic.title || "Topic",
        dateRange: badgeTopic.timespan || "",
        accentColor: this.getTopicColor(badgeTopic)
      };
    },
    topicLayerStyle(topic, unit) {
      const profile = this.getTopicVisualProfile(topic);
      if (!profile) {
        return {};
      }
      const topicId = typeof topic === "object" && topic !== null ? topic.id : topic;
      const focused = this.isFocusedTopic(topic, unit);
      const hovered = this.hoveredTopicId === topicId;
      const emphasized = focused || hovered;
      return {
        "--topic-accent": profile.accentColor,
        "--topic-accent-soft": this.hexToRgba(profile.accentColor, emphasized ? 0.28 : 0.12),
        "--topic-layer-opacity": emphasized ? 1 : 0.22,
        "--topic-glow-opacity": emphasized ? 1 : 0.35
      };
    },
    overlapContourStyle(region, unit) {
      const profile = this.getTopicVisualProfile(region?.topic);
      if (!profile) {
        return {};
      }
      const focused = this.isFocusedTopic(region.topic, unit);
      const hovered = this.hoveredTopicId === region.topic.id;
      const emphasized = focused || hovered;
      const mode = profile.mapTreatmentType;
      const dashByMode = {
        "idea-network": "",
        "heatmap-contour": "6 5",
        "global-flow": "10 7",
        "planned-grid": "3 4"
      };
      const strokeByMode = {
        "idea-network": focused ? 1.55 : 0.95,
        "heatmap-contour": focused ? 2.05 : 1.15,
        "global-flow": focused ? 2.2 : 1.2,
        "planned-grid": focused ? 2 : 1.1
      };
      return {
        stroke: hovered ? "#ffffff" : profile.accentColor,
        strokeWidth: (strokeByMode[mode] || (focused ? 2 : 1.15)) + (hovered ? 0.25 : 0),
        fill: this.hexToRgba(profile.accentColor, emphasized ? 0.46 : 0.03),
        strokeDasharray: dashByMode[mode] || "",
        opacity: emphasized ? 1 : 0.55
      };
    },
    resolveTopicImage(topic) {
      const imageCandidates = [
        topic?.topic_thumbURL,
        topic?.intro_thumbURL,
        topic?.topic_thumbFile,
        topic?.intro_thumbFile
      ];

      const imagePath = imageCandidates.find(
        value => typeof value === "string" && value.trim().length > 0
      );

      return imagePath ? imagePath.trim() : "";
    },
    topicCardStyle(topic) {
      const color = this.getTopicColor(topic);
      const topicImage = this.resolveTopicImage(topic);

      const overlayGradient =
        "linear-gradient(180deg, rgba(6, 6, 9, 0.3) 0%, rgba(6, 6, 9, 0.55) 52%, rgba(6, 6, 9, 0.78) 100%)";

      return {
        borderColor: this.hexToRgba(color, 0.72),
        backgroundImage: topicImage
          ? `${overlayGradient}, url("${topicImage}")`
          : `linear-gradient(180deg, ${this.hexToRgba(
              color,
              0.66
            )} 0%, rgba(6, 6, 9, 0.9) 100%)`
      };
    },
    positionTooltip(event) {
      if (!event) {
        return;
      }
      if (!this.activeMapRect) {
        this.setActiveMapRect(event);
      }
      const rect = this.activeMapRect;
      if (!rect) {
        return;
      }

      const clientX =
        typeof event.clientX === "number"
          ? event.clientX
          : rect.left + rect.width / 2;
      const clientY =
        typeof event.clientY === "number"
          ? event.clientY
          : rect.top + rect.height / 2;

      const desiredX = clientX - rect.left + 14;
      const desiredY = clientY - rect.top - 18;

      this.hoverTooltip.x = Math.max(14, Math.min(rect.width - 230, desiredX));
      this.hoverTooltip.y = Math.max(14, Math.min(rect.height - 80, desiredY));
    },
    setHoveredRegion(region, event) {
      this.hoveredTopicId = region.topic.id;
      this.hoverTooltip.visible = true;
      this.hoverTooltip.title = region.topic.title;
      this.hoverTooltip.timespan = region.topic.timespan || "";
      this.hoverTooltip.color = this.getTopicColor(region.topic);
      this.setActiveMapRect(event);
      this.positionTooltip(event);
    },
    moveTooltip(event) {
      if (!this.hoverTooltip.visible) {
        return;
      }
      this.pendingTooltipPosition = {
        clientX: event.clientX,
        clientY: event.clientY,
        currentTarget: event.currentTarget,
        target: event.target
      };

      if (this.tooltipRafId !== null) {
        return;
      }

      this.tooltipRafId = window.requestAnimationFrame(() => {
        if (this.pendingTooltipPosition) {
          this.positionTooltip(this.pendingTooltipPosition);
        }
        this.pendingTooltipPosition = null;
        this.tooltipRafId = null;
      });
    },
    buildWorldMapPaths() {
      const width = 1000;
      const height = 520;

      const projection = d3.geo
        .equirectangular()
        .scale(159)
        .translate([width / 2, height / 2]);
      const path = d3.geo.path().projection(projection);

      const world = countries;
      const geometries = world?.objects?.units?.geometries || [];

      const land = topojson.merge(
        world,
        geometries.filter(geometry => geometry.id !== "ATA")
      );

      const borders = topojson.mesh(
        world,
        world.objects.units,
        (a, b) => a !== b && a.id !== "ATA" && b.id !== "ATA"
      );

      this.worldLandPath = path(land) || "";
      this.worldBorderPath = path(borders) || "";
      this.geoPath = path;
      this.countryGeometries = geometries;
      this.geometryById = new Map(
        geometries
          .filter(geometry => geometry?.id)
          .map(geometry => [geometry.id, geometry])
      );

      // Project the actual 13 colonies boundary using the same equirectangular projection.
      // Coordinates trace the Atlantic coastline south from Maine to Georgia,
      // then the western boundary (Appalachian / Proclamation Line) back north.
      const thirteenColoniesFeature = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[
            [-67.0, 47.5],  // Maine NE (NB border)
            [-67.6, 44.5],  // Maine SE coast
            [-70.9, 43.1],  // NH / ME coast
            [-70.8, 42.6],  // MA north shore
            [-71.3, 41.5],  // RI coast
            [-73.7, 41.1],  // CT / NY coast
            [-74.0, 40.7],  // NYC / Long Island
            [-74.3, 39.5],  // NJ central coast
            [-74.9, 38.9],  // Cape May NJ
            [-75.0, 38.5],  // Delaware
            [-75.2, 38.0],  // MD eastern shore
            [-76.0, 37.0],  // VA cape
            [-75.5, 35.8],  // NC outer banks
            [-76.9, 34.6],  // NC south coast
            [-78.5, 33.9],  // NC / SC
            [-79.5, 33.1],  // SC coast
            [-80.9, 32.0],  // SC / GA
            [-81.3, 31.3],  // GA coast
            [-81.5, 30.4],  // GA south / FL border
            [-84.9, 30.4],  // GA SW corner
            [-85.5, 35.0],  // GA / AL / TN western extent
            [-84.3, 35.1],  // GA / TN border
            [-84.0, 36.5],  // NC / TN / VA Appalachians
            [-83.0, 37.0],  // VA / KY Appalachians
            [-80.5, 39.7],  // PA / WV western border
            [-80.5, 42.0],  // PA NW corner
            [-79.1, 43.3],  // NY / Ontario (Niagara area)
            [-79.0, 45.0],  // NY / QC border
            [-72.5, 45.0],  // VT / QC border (45th parallel)
            [-71.5, 45.3],  // NH / ME / QC junction
            [-67.0, 47.5]   // back to Maine NE
          ]]
        }
      };
      this.thirteenColoniesPath = path(thirteenColoniesFeature) || "";

      this.rebuildUnitRegionCache();
    },
    enabledTopics(unit) {
      if (!unit || !Array.isArray(unit.topics)) {
        return [];
      }
      // Show topics unless explicitly disabled (enabled === false).
      // Topics missing the field are treated as visible.
      return unit.topics.filter(topic => topic.enabled !== false);
    },
    normalizedTitle(topic) {
      return String(topic?.title || "")
        .toLowerCase()
        .trim();
    },
    getRegionKeyForTopic(topic, unit = null) {
      const title = this.normalizedTitle(topic);
      const unitHeader = String(unit?.unitHeader || topic?.unit || "").toLowerCase();
      const regionMatchers = [
        {
          region: "world",
          keywords: [
            "cold war",
            "wwii",
            "world war ii",
            "second world war",
            "world war 2",
            "wwi",
            "world war i",
            "first world war",
            "world war 1"
          ]
        },
        {
          region: "europe",
          keywords: ["enlightenment"]
        },
        {
          region: "europe",
          keywords: ["industrial revolutions", "industrial revolution"]
        },
        {
          region: "europe",
          keywords: ["consequences of industrialization"]
        },
        {
          region: "russiaCentralAsia",
          keywords: ["state-sponsored industrialization", "state sponsored industrialization"]
        },
        {
          region: "franceOnly",
          keywords: ["french revolution"]
        },
        {
          region: "thirteenColonies",
          keywords: [
            "american revolution",
            "american revoltion",
            "ameria revolution",
            "ameria revoltion",
            "american independence",
            "thirteen colonies",
            "13 colonies"
          ]
        },
        {
          region: "wwii",
          keywords: [
            "wwii",
            "world war ii",
            "second world war",
            "world war 2"
          ]
        },
        {
          region: "mongolEmpire",
          keywords: ["mongol empire", "mongol", "yuan dynasty", "yuan"]
        },
        {
          region: "silkRoad",
          keywords: [
            "silk road",
            "silk roads",
            "silk route",
            "silk routes",
            "trans-eurasian",
            "eurasian trade"
          ]
        },
        {
          region: "darAlIslam",
          keywords: [
            "dar al-islam",
            "dar al islam",
            "middle east",
            "islamic",
            "ottoman",
            "safavid",
            "abbasid"
          ]
        },
        {
          region: "southSoutheastAsia",
          keywords: [
            "south and southeast asia",
            "south asia",
            "southeast asia",
            "india",
            "mughal",
            "indian ocean"
          ]
        },
        {
          region: "mesoamerica",
          keywords: ["mesoamerica", "aztec", "maya", "olmec"]
        },
        {
          region: "southAmerica",
          keywords: [
            "latin and south america",
            "south america",
            "anti-colonialism in latin and south america"
          ]
        },
        {
          region: "americas",
          keywords: [
            "americas",
            "latin america",
            "south america",
            "north america",
            "caribbean",
            "inca"
          ]
        },
        {
          region: "eastAsia",
          keywords: ["east asia", "japan", "korea", "mongol", "yuan", "qing"]
        },
        {
          region: "china",
          keywords: ["china", "song", "ming"]
        },
        {
          region: "europe",
          keywords: [
            "europe",
            "western europe",
            "eastern europe",
            "renaissance",
            "enlightenment",
            "industrial",
            "atlantic"
          ]
        },
        {
          region: "africa",
          keywords: [
            "africa",
            "sub-saharan",
            "west africa",
            "east africa",
            "north africa",
            "sahel"
          ]
        },
        {
          region: "russiaCentralAsia",
          keywords: ["russia", "siberia", "central asia", "steppe"]
        },
        {
          region: "oceania",
          keywords: ["oceania", "australia", "pacific", "polynesia"]
        }
      ];

      const matched = regionMatchers.find(entry =>
        entry.keywords.some(keyword => title.includes(keyword))
      );

      if (matched) {
        return matched.region;
      }

      if (
        unitHeader.includes("unit 5") &&
        title.includes("revolution") &&
        (title.includes("american") || title.includes("colonies") || title.includes("independence"))
      ) {
        return "thirteenColonies";
      }

      if (
        title.includes("american") &&
        ["revolution", "independence", "colonies", "thirteen", "13 colonies"].some(keyword => title.includes(keyword))
      ) {
        return "thirteenColonies";
      }

      const fallbackRegions = [
        "europe",
        "africa",
        "eastAsia",
        "southSoutheastAsia",
        "americas",
        "darAlIslam",
        "russiaCentralAsia",
        "oceania"
      ];
      const fallbackIndex =
        this.hashTopicId(topic?.id || topic?.title) % fallbackRegions.length;
      return fallbackRegions[fallbackIndex];
    },
    rebuildUnitRegionCache() {
      if (!this.geoPath || !this.countryGeometries.length) {
        this.unitRegionCache = {};
        return [];
      }

      const unitsArray = Array.isArray(this.units)
        ? this.units
        : Object.values(this.units || {});

      const nextCache = {};

      unitsArray.forEach(unit => {
        const key = this.getUnitCacheKey(unit);
        const topics = this.enabledTopics(unit);

        nextCache[key] = topics
          .map(topic => {
            const regionKey = this.getRegionKeyForTopic(topic, unit);
            if (!regionKey) {
              return null;
            }

            const countryIds = this.regionCountryMap[regionKey] || [];
            const resolvedCountryIds =
              regionKey === "world"
                ? Array.from(this.geometryById.keys()).filter(countryId => countryId !== "ATA")
                : countryIds;
            if (!resolvedCountryIds.length) {
              return null;
            }

            const matchedGeometries = resolvedCountryIds
              .map(countryId => this.geometryById.get(countryId))
              .filter(Boolean);

            if (!matchedGeometries.length) {
              return null;
            }

            const mergedShape = topojson.merge(countries, matchedGeometries);
            const regionPath = this.geoPath(mergedShape) || "";
            if (!regionPath) {
              return null;
            }

            return {
              topic,
              regionKey,
              path: regionPath
            };
          })
          .filter(Boolean);
      });

      this.unitRegionCache = nextCache;
      return nextCache;
    },
    mapRegionsForUnit(unit) {
      const key = this.getUnitCacheKey(unit);
      return this.unitRegionCache[key] || [];
    },
    silkRoadRegionsForUnit(unit) {
      return this.mapRegionsForUnit(unit).filter(
        region => region.regionKey === "silkRoad"
      );
    },
    thirteenColoniesRegionsForUnit(unit) {
      return this.mapRegionsForUnit(unit).filter(
        region => region.regionKey === "thirteenColonies"
      );
    },
    franceOnlyRegionsForUnit(unit) {
      return this.mapRegionsForUnit(unit).filter(
        region => region.regionKey === "franceOnly"
      );
    },
    wwiiRegionsForUnit(unit) {
      return this.mapRegionsForUnit(unit).filter(
        region => region.regionKey === "wwii"
      );
    },
    overlapAwareRegionsForUnit(unit) {
      return this.mapRegionsForUnit(unit).filter(region =>
        this.isOverlapAwareTopic(region.topic)
      );
    },
    nonSpecialOverlayRegionsForUnit(unit) {
      return this.mapRegionsForUnit(unit).filter(
        region =>
          !["silkRoad", "thirteenColonies", "wwii", "franceOnly"].includes(region.regionKey) &&
          !this.isOverlapAwareTopic(region.topic)
      );
    },
    isIndustrialFocused(unit) {
      const focusId = this.mapFocusTopicIdForUnit(unit);
      if (!focusId) return false;
      const focusedRegion = this.overlapAwareRegionsForUnit(unit).find(r => r.topic.id === focusId);
      if (!focusedRegion) return false;
      return this.topicRenderMode(focusedRegion.topic) === "heatmap-contour";
    },
    setHoveredTopic(topicId) {
      this.hoveredTopicId = topicId;
    },
    clearHoveredTopic() {
      this.hoveredTopicId = null;
      this.hoverTooltip.visible = false;
      this.activeMapRect = null;
      this.pendingTooltipPosition = null;
      if (this.tooltipRafId !== null) {
        window.cancelAnimationFrame(this.tooltipRafId);
        this.tooltipRafId = null;
      }
    },
    back() {
      pushRoute("All_Periods");
    },
    next(topic) {
      storeTopic.dispatch("setTopicContent", topic);
      // Use goToTopic to ensure topicName and periodName are slugified for SEO-friendly URLs
      const periodName =
        this.timePeriodHeaders && this.timePeriodHeaders.header;
      const periodId =
        this.$route.params.period ||
        this.$store?.state?.currentTimePeriod ||
        null;
      goToTopic(
        periodName,
        periodId,
        topic.title,
        topic.id,
        "Introduction",
        topic.title
      );
    }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: "smooth" });
    this.buildWorldMapPaths();
    if (this.units.length == 0) {
      console.log("this.$route.params.period", this.$route.params.period);
      store.dispatch("setTimePeriod", this.$route.params.period);
    }
  },
  beforeUnmount() {
    if (this.tooltipRafId !== null) {
      window.cancelAnimationFrame(this.tooltipRafId);
      this.tooltipRafId = null;
    }
  }
};
</script>

<!-- <style lang="sass" scoped src="@/assets/css/topics.sass"></style> -->

<style lang="sass" scoped>
@import "@/assets/css/topics.sass"

.page_header_row
  min-height: 132px
  padding: 16px 22px

.page_header_inner
  min-height: 100px
  width: 100%
  display: flex
  align-items: center
  gap: 14px

.header_content_stack
  min-width: 0
  display: flex
  flex-direction: column
  gap: 8px

.header_back_button
  display: inline-flex
  align-items: center
  gap: 6px
  padding: 6px 14px
  font-family: "Montserrat", sans-serif
  font-weight: 700
  font-size: 14px
  color: #fff
  background: rgba(91, 33, 182, 0.4)
  border: 1px solid rgba(91, 33, 182, 1)
  border-radius: 10px
  backdrop-filter: blur(6px)
  text-decoration: none
  cursor: pointer
  transition: all 0.25s ease

.header_back_button:hover
  background: #5B21B6
  transform: translateY(-1px)

.header_back_button:active
  transform: translateY(0)

.header_breadcrumb
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 6px
  min-width: 0
  color: #ffffff
  font-family: "Montserrat", sans-serif
  letter-spacing: -0.4px
  font-size: 22px
  line-height: 1.2
  font-weight: 500

.crumb_item
  max-width: 100%
  word-break: break-word

.crumb_current
  font-weight: 700

.crumb_sep
  color: rgba(255, 255, 255, 0.82)
  font-size: 20px
  line-height: 1

.home-periods__heading
  color: #ffffff
  font-family: "Montserrat", sans-serif

.home-periods__title
  margin: 0
  line-height: 1.1
  letter-spacing: -0.45px
  font-size: clamp(20px, 2vw, 30px)
  font-weight: 700

.home-periods__subtitle
  margin: 4px 0 0
  line-height: 1.2
  letter-spacing: -0.2px
  font-size: clamp(12px, 1.1vw, 16px)
  opacity: 0.95

.topics_container
  max-width: 1360px
  margin: 0 auto
  padding: 32px 20px 44px

.topics_unit_row
  margin: 0

.topics_content_layout
  display: grid
  grid-template-columns: minmax(260px, 336px) minmax(0, 1fr)
  align-items: start
  gap: 24px

.topics_left_panel
  display: flex
  flex-direction: column
  gap: 18px

.unit_header
  margin: 0
  color: white !important
  font-family: "Montserrat", sans-serif
  letter-spacing: -0.6px
  line-height: 1.1
  font-weight: 500
  font-size: clamp(34px, 2.8vw, 48px)
  text-align: center

.topic_cards_grid
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 14px

.topic_card_tile
  position: relative
  width: 100%
  aspect-ratio: 1 / 1
  border: 1px solid rgba(255, 255, 255, 0.2)
  border-radius: 24px
  overflow: hidden
  padding: 16px 12px 15px
  background-color: #111827
  background-size: cover
  background-repeat: no-repeat
  background-position: center
  color: #ffffff
  text-align: center
  display: flex
  align-items: flex-end
  justify-content: center
  cursor: pointer
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.24)
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease, border-color 0.2s ease

.topic_card_tile::before
  content: ""
  position: absolute
  inset: 0
  background: rgba(8, 8, 12, 0.2)
  pointer-events: none

.topic_card_tile:hover,
.topic_card_tile.hovered,
.topic_card_tile:focus-visible
  transform: translateY(-2px)
  filter: brightness(1.06)
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.36)
  outline: none

.topic_card_tile:focus-visible
  border-color: #ffffff

.topic_card_tile.active
  border-color: rgba(255, 255, 255, 0.9)
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.36), 0 0 0 2px rgba(255, 255, 255, 0.42)

.topic_card_accent
  position: absolute
  z-index: 1
  top: 16px
  left: 20px
  right: 20px
  height: 14px
  border-radius: 999px
  opacity: 0.95
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.2), 0 6px 18px rgba(0, 0, 0, 0.35)

.topic_card_body
  position: relative
  z-index: 1
  width: 100%
  display: flex
  flex-direction: column
  align-items: center
  justify-content: flex-end
  gap: 6px
  color: #ffffff
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.82)

.topic_card_title
  margin: 0
  font-family: "Montserrat", sans-serif
  letter-spacing: -0.25px
  line-height: 1.12
  font-size: clamp(13px, 1.09vw, 22px)
  font-weight: 700
  max-width: 94%
  word-break: normal
  overflow-wrap: break-word

.topic_card_time
  margin: 0
  font-family: "Montserrat", sans-serif
  letter-spacing: -0.2px
  line-height: 1.16
  font-size: clamp(12px, 0.92vw, 17px)
  font-weight: 500
  max-width: 94%
  opacity: 0.98

.topics_right_panel
  min-width: 0

.topics_map
  position: relative
  width: 100%
  aspect-ratio: 1000 / 520
  border-radius: 24px
  overflow: hidden
  border: 1px solid rgba(24, 24, 27, 0.62)
  background: #050507
  box-shadow: 0 18px 38px rgba(0, 0, 0, 0.3)

.world_svg
  width: 100%
  height: 100%
  display: block

.ocean
  fill: #050507

.world_land
  fill: #4a4b50
  stroke: #2f3035
  stroke-width: 1

.world_borders
  fill: none
  stroke: #27282d
  stroke-width: 0.55
  opacity: 0.85

.map_highlight
  cursor: pointer
  transition: filter 0.2s ease

.map_highlight.hovered
  filter: brightness(1.15)

.map_highlight.world_outline,
.map_highlight.world_outline.hovered,
.map_highlight.world_outline:focus
  filter: none

.map_highlight:focus
  outline: none
  filter: brightness(1.18)

.overlap_aware_layer
  cursor: pointer
  opacity: var(--topic-layer-opacity, 0.22)
  transition: opacity 0.28s ease, filter 0.28s ease

.overlap_aware_layer.active
  opacity: var(--topic-layer-opacity, 1)
  filter: drop-shadow(0 0 6px color-mix(in srgb, var(--topic-accent, #ffffff), transparent 30%))

.overlap_aware_layer.hovered
  opacity: 1
  filter: drop-shadow(0 0 8px color-mix(in srgb, var(--topic-accent, #ffffff), transparent 20%)) brightness(1.12)

.overlap_aware_layer.ghost
  opacity: var(--topic-layer-opacity, 0.22)

.overlap_contour
  stroke-linejoin: round
  transition: stroke-width 0.26s ease, opacity 0.26s ease, fill 0.26s ease

.idea_route
  fill: none
  stroke: var(--topic-accent, #E7B95A)
  stroke-width: 1.6
  stroke-linecap: round
  stroke-dasharray: 2 3
  opacity: calc(0.5 * var(--topic-glow-opacity, 1))

.idea_node_glow
  display: none

.idea_node
  display: none

.overlap_aware_layer.active .idea_node_glow,
.overlap_aware_layer.hovered .idea_node_glow
  opacity: calc(0.92 * var(--topic-glow-opacity, 1))

.overlap_aware_layer.active .idea_node,
.overlap_aware_layer.hovered .idea_node
  opacity: calc(0.95 * var(--topic-glow-opacity, 1))

.industrial_heat_zone
  fill: var(--topic-accent-soft, rgba(196, 106, 40, 0.22))
  stroke: var(--topic-accent, #C46A28)
  stroke-width: 0.95
  opacity: calc(0.68 * var(--topic-glow-opacity, 1))

.industrial_factory_symbol
  fill: color-mix(in srgb, var(--topic-accent, #C46A28), #171717 35%)
  stroke: rgba(255, 255, 255, 0.48)
  stroke-width: 0.55
  opacity: calc(0.9 * var(--topic-glow-opacity, 1))

.industrial_spread_arc
  fill: none
  stroke: var(--topic-accent, #C46A28)
  stroke-width: 1.8
  stroke-linecap: round
  stroke-dasharray: 8 5
  opacity: calc(0.72 * var(--topic-glow-opacity, 1))

.industrial_spread_node
  display: none

.industrial_city_label
  font-family: "Montserrat", sans-serif
  font-size: 9px
  font-weight: 600
  letter-spacing: 0.2px
  fill: rgba(255, 248, 235, 0.9)
  paint-order: stroke
  stroke: rgba(5, 5, 7, 0.75)
  stroke-width: 2.5
  pointer-events: none
  opacity: calc(0.9 * var(--topic-glow-opacity, 1))

.global_flow_arc
  fill: none
  stroke: var(--topic-accent, #4CC9B0)
  stroke-width: 1.8
  stroke-linecap: round
  stroke-dasharray: 8 5
  opacity: calc(0.7 * var(--topic-glow-opacity, 1))

.global_flow_node
  fill: var(--topic-accent, #4CC9B0)
  stroke: rgba(255, 255, 255, 0.72)
  stroke-width: 0.8

.state_grid_segment
  fill: none
  stroke: color-mix(in srgb, var(--topic-accent, #D84A4A), #ffffff 10%)
  stroke-width: 0.95
  stroke-dasharray: 5 4
  opacity: calc(0.78 * var(--topic-glow-opacity, 1))

.state_corridor
  fill: none
  stroke: var(--topic-accent, #D84A4A)
  stroke-width: 2.2
  stroke-linecap: round
  opacity: calc(0.7 * var(--topic-glow-opacity, 1))

.map_mode_badge
  position: absolute
  left: 14px
  top: 14px
  z-index: 2
  display: flex
  align-items: center
  gap: 12px
  padding: 8px 10px
  border-radius: 14px
  border: 2px solid rgba(255, 255, 255, 0.25)
  background: rgba(7, 8, 12, 0.8)
  backdrop-filter: blur(6px)
  box-shadow: 2px 2px 4px rgba(255, 255, 255, 0.25)

.badge_dot
  width: 14px
  height: 14px
  border-radius: 999px
  background: var(--badge-accent, #ffffff)
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.24)

.badge_text
  display: flex
  flex-direction: column

.badge_title
  margin: 0
  font-family: "Montserrat", sans-serif
  font-size: 15px
  font-weight: 700
  letter-spacing: -0.1px
  color: #ffffff

.badge_subtitle
  margin: 2px 0 0
  font-family: "Montserrat", sans-serif
  font-size: 13px
  font-weight: 500
  color: #d4d4d8

.silk_road_overlay
  cursor: pointer

.map_silk_route
  fill: none
  stroke-width: 5
  stroke-linecap: round
  stroke-linejoin: round
  opacity: 0.45
  transition: opacity 0.2s ease, stroke-width 0.2s ease

.silk_road_overlay.hovered .map_silk_route,
.silk_road_overlay:focus .map_silk_route
  opacity: 1
  stroke-width: 5.5

.silk_city_dot
  stroke: rgba(255, 255, 255, 0.7)
  stroke-width: 2
  opacity: 0.45
  transition: opacity 0.2s ease

.silk_road_overlay.hovered .silk_city_dot,
.silk_road_overlay:focus .silk_city_dot
  opacity: 1

.silk_city_label
  font-family: "Georgia", "Times New Roman", serif
  font-size: 12px
  font-weight: 600
  fill: #ffffff
  paint-order: stroke
  stroke: rgba(5, 5, 7, 0.96)
  stroke-width: 1.3
  pointer-events: none
  opacity: 0
  transition: opacity 0.2s ease

.silk_road_overlay.hovered .silk_city_label,
.silk_road_overlay:focus .silk_city_label
  opacity: 1

.thirteen_colonies_area
  fill: transparent
  stroke-linejoin: round
  stroke-linecap: round

.france_only_area
  stroke-width: 1.7

.wwii_land_zone
  stroke-width: 1.25

.wwii_ocean_zone
  stroke-width: 1.1

.silk_region_label
  font-family: "Georgia", "Times New Roman", serif
  font-size: 29px
  letter-spacing: 2.4px
  fill: rgba(245, 238, 218, 0.88)
  pointer-events: none

.silk_water_label
  font-family: "Georgia", "Times New Roman", serif
  font-size: 23px
  font-style: italic
  letter-spacing: 1px
  fill: rgba(203, 218, 236, 0.82)
  pointer-events: none

.map_tooltip
  position: absolute
  z-index: 2
  min-width: 190px
  max-width: 230px
  background: rgba(8, 8, 10, 0.95)
  color: #ffffff
  border: 2px solid
  border-radius: 10px
  padding: 10px 12px
  pointer-events: none
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.45)

.tooltip_title
  margin: 0
  font-family: "Montserrat", sans-serif
  font-size: 14px
  font-weight: 700
  line-height: 1.25

.tooltip_time
  margin: 3px 0 0 0
  font-family: "Montserrat", sans-serif
  font-size: 13px
  line-height: 1.2
  color: #e2e8f0

@media (max-width: 920px)
  .page_header_row
    min-height: 124px
    padding: 14px 14px

  .page_header_inner
    gap: 10px

  .header_breadcrumb
    font-size: 18px

  .topics_container
    padding: 26px 14px 36px

  .topics_content_layout
    grid-template-columns: minmax(220px, 280px) minmax(0, 1fr)
    gap: 16px

  .unit_header
    font-size: clamp(28px, 4vw, 36px)

  .topic_cards_grid
    gap: 10px

  .topic_card_tile
    border-radius: 20px
    padding: 14px 10px 12px

  .topic_card_accent
    top: 12px
    left: 14px
    right: 14px
    height: 12px

  .topic_card_title
    font-size: clamp(13px, 1.55vw, 19px)

  .topic_card_time
    font-size: clamp(11px, 1.1vw, 14px)

@media (max-width: 760px)
  .topics_content_layout
    grid-template-columns: 1fr

  .topics_right_panel
    margin-top: 4px

  .map_mode_badge
    left: 10px
    top: 10px
    padding: 9px 12px
    gap: 10px

  .badge_title
    font-size: 13px

  .badge_subtitle
    font-size: 12px

@media (max-width: 576px)
  .page_header_inner
    align-items: flex-start
    flex-direction: column
    justify-content: center

  .header_back_button
    padding: 5px 10px
    font-size: 13px

  .header_breadcrumb
    font-size: 16px
    gap: 4px

  .topics_container
    padding: 20px 10px 28px

  .unit_header
    font-size: clamp(24px, 8vw, 32px)

  .topic_cards_grid
    gap: 9px

  .topic_card_tile
    border-radius: 18px
    padding: 11px 8px 10px

  .topic_card_accent
    top: 10px
    left: 12px
    right: 12px
    height: 10px

  .topic_card_title
    font-size: clamp(12px, 3.8vw, 17px)

  .topic_card_time
    font-size: clamp(11px, 2.8vw, 14px)

@media (prefers-reduced-motion: reduce)
  .topic_card_tile,
  .map_highlight,
  .overlap_aware_layer,
  .overlap_contour,
  .map_silk_route,
  .silk_city_dot,
  .silk_city_label
    transition: none !important
</style>